import { ApplicationInsights } from '@microsoft/applicationinsights-web'

let appInsights = null

// Define connection strings for different environments
const CONNECTION_STRINGS = {
  production:
    'InstrumentationKey=72ef0c7c-aa1f-4482-8726-179d89b28e94;IngestionEndpoint=https://swedencentral-0.in.applicationinsights.azure.com/;LiveEndpoint=https://swedencentral.livediagnostics.monitor.azure.com/;ApplicationId=5ec081e0-57b6-4957-a810-dedf262283f3',
  staging:
    'InstrumentationKey=5bd8b0da-e178-41fb-9bca-ff1637eb12bb;IngestionEndpoint=https://swedencentral-0.in.applicationinsights.azure.com/;LiveEndpoint=https://swedencentral.livediagnostics.monitor.azure.com/;ApplicationId=cd372535-8464-4934-beb2-198c0e116baa',
}

/**
 * Determines if the current environment is staging
 * This can be customized based on how you identify staging vs production
 */
export const isStaging = () => {
  // Check for specific URL patterns
  const isTestStaging = window.location.href.includes('testStaging=true')
  const isStagingDomain = window.location.hostname.includes('staging')

  // You can add more conditions based on your deployment setup
  return isTestStaging || isStagingDomain
}

/**
 * Get the appropriate connection string based on environment
 */
export const getConnectionString = () => {
  return isStaging() ? CONNECTION_STRINGS.staging : CONNECTION_STRINGS.production
}

/**
 * Get the Application Insights instance
 * @returns {ApplicationInsights} The Application Insights instance
 */
export const getAppInsights = () => appInsights

export const initAppInsights = (connectionStringOverride) => {
  // Use provided connection string or determine from environment
  const connectionString = connectionStringOverride || getConnectionString()

  if (!connectionString) {
    console.warn('No connection string provided for Application Insights')
    return null
  }

  appInsights = new ApplicationInsights({
    config: {
      connectionString: connectionString,
      enableAutoRouteTracking: true, // Automatically track route changes
      disableFetchTracking: false, // Track AJAX requests
      enableCorsCorrelation: true, // Enable correlation with server side
      autoTrackPageVisitTime: true, // Track how long users stay on pages
      disableExceptionTracking: false, // Track unhandled exceptions
    },
  })

  appInsights.loadAppInsights()
  appInsights.trackPageView() // Track the initial page view

  return appInsights
}

/**
 * Set authenticated user context
 * @param {string} userId - The user's unique identifier (UserDbId)
 * @param {string} accountId - Optional account/company ID
 * @param {boolean} storeInCookie - Whether to store in cookie (default: false)
 */
export const setAuthenticatedUserContext = (userId, accountId = undefined, storeInCookie = false) => {
  const instance = getAppInsights()
  if (instance && userId) {
    try {
      instance.clearAuthenticatedUserContext()

      instance.setAuthenticatedUserContext(userId, accountId, storeInCookie)

      instance.trackEvent('UserContextSet', {
        userIdAttempt: userId,
        timestamp: new Date().toISOString(),
      })

      return true
    } catch (error) {
      console.error('Error setting authenticated user context:', error)
      return false
    }
  }
  return false
}

export const trackPageView = (name, url) => {
  if (appInsights) {
    appInsights.trackPageView({ name, uri: url })
  }
}

export const trackEvent = (name, properties = {}) => {
  if (appInsights) {
    appInsights.trackEvent({ name }, properties)
  }
}

export const trackException = (error, severityLevel = 3) => {
  if (appInsights && error) {
    appInsights.trackException({ exception: error, severityLevel })
  }
}

export default {
  appInsights,
  trackPageView,
  trackEvent,
  trackException,
  isStaging,
  getConnectionString,
  getAppInsights,
  setAuthenticatedUserContext,
}
